/**
 * Activity Tab With Modals
 *
 * Self-contained wrapper around ActivityTab that manages all activity logging modals.
 * Includes LogCallModal, LogMeetingModal, LogTaskModal, AddNoteModal, and EditActivityModal.
 * Supports edit/delete functionality for activities owned by the current user.
 * Used by DynamicSectionRenderer for the 'activity' section type.
 */

import React, { useState, useCallback } from 'react';
import { ActivityTab, LogActivityType } from './ActivityTab';
import { LogCallModal } from '../modals/LogCallModal';
import { LogMeetingModal } from '../modals/LogMeetingModal';
import { LogTaskModal } from '../modals/LogTaskModal';
import { AddNoteModal } from '../modals/AddNoteModal';
import { EditActivityModal } from '../modals/EditActivityModal';
import { Activity, RecordType } from '../../types/activity';
import { activityStorage } from '../../services/activityStorage';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useToast } from '../../contexts/ToastContext';

interface ActivityTabWithModalsProps {
  recordId?: string;
  recordType?: RecordType;
  contactName?: string;
  phoneNumber?: string;
  relatedToName?: string;
  className?: string;
}

export const ActivityTabWithModals: React.FC<ActivityTabWithModalsProps> = ({
  recordId,
  recordType = 'client',
  contactName,
  phoneNumber,
  relatedToName,
  className = '',
}) => {
  const { userId } = useCurrentUser();
  const { success, error: showError } = useToast();

  // Log activity modal state
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);

  // Edit/Delete state
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [deletingActivity, setDeletingActivity] = useState<Activity | null>(null);

  // Force refresh key to re-render ActivityTab after changes
  const [refreshKey, setRefreshKey] = useState(0);

  // Handle log activity dropdown selection
  const handleLogActivity = useCallback((type: LogActivityType) => {
    switch (type) {
      case 'call':
        setShowCallModal(true);
        break;
      case 'meeting':
        setShowMeetingModal(true);
        break;
      case 'task':
        setShowTaskModal(true);
        break;
      case 'note':
        setShowNoteModal(true);
        break;
    }
  }, []);

  // Refresh activity list after any change
  const handleActivityChanged = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Handle edit activity
  const handleEditActivity = useCallback((activity: Activity) => {
    setEditingActivity(activity);
  }, []);

  // Handle delete activity
  const handleDeleteActivity = useCallback((activity: Activity) => {
    setDeletingActivity(activity);
  }, []);

  // Confirm delete (async for Gobodo API)
  const confirmDelete = useCallback(async () => {
    if (!deletingActivity) return;

    try {
      const deleted = await activityStorage.delete(deletingActivity.id);
      if (deleted) {
        success('Activity deleted', 'The activity has been removed');
        handleActivityChanged();
      } else {
        showError('Delete failed', 'Could not delete the activity');
      }
    } catch (error) {
      console.error('[ActivityTabWithModals] Delete failed:', error);
      showError('Delete failed', 'Could not delete the activity');
    }
    setDeletingActivity(null);
  }, [deletingActivity, success, showError, handleActivityChanged]);

  // Don't render modals if we don't have a recordId
  if (!recordId) {
    return (
      <div className={className}>
        <ActivityTab
          recordId={recordId}
          recordType={recordType}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Activity Tab with Log Activity dropdown and edit/delete support */}
      <ActivityTab
        key={refreshKey}
        recordId={recordId}
        recordType={recordType}
        currentUserId={userId}
        onLogActivity={handleLogActivity}
        onEditActivity={handleEditActivity}
        onDeleteActivity={handleDeleteActivity}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={recordId}
        recordType={recordType}
        contactName={contactName}
        phoneNumber={phoneNumber}
        onCallLogged={handleActivityChanged}
      />

      {/* Log Meeting Modal - also used for editing meetings */}
      <LogMeetingModal
        isOpen={showMeetingModal || (!!editingActivity && editingActivity.type === 'meeting')}
        onClose={() => {
          setShowMeetingModal(false);
          if (editingActivity?.type === 'meeting') {
            setEditingActivity(null);
          }
        }}
        recordId={recordId}
        recordType={recordType}
        contactName={contactName}
        relatedToName={relatedToName || contactName}
        onMeetingLogged={handleActivityChanged}
        editingActivity={editingActivity?.type === 'meeting' ? editingActivity : null}
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={recordId}
        recordType={recordType}
        contactName={contactName}
        onTaskLogged={handleActivityChanged}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={recordId}
        recordType={recordType}
        contactName={contactName}
        onNoteAdded={handleActivityChanged}
      />

      {/* Edit Activity Modal - for non-meeting activities (calls, tasks, notes) */}
      <EditActivityModal
        isOpen={!!editingActivity && editingActivity.type !== 'meeting'}
        onClose={() => setEditingActivity(null)}
        activity={editingActivity}
        onActivityUpdated={handleActivityChanged}
      />

      {/* Delete Confirmation Modal */}
      {deletingActivity && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setDeletingActivity(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Delete Activity?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this {deletingActivity.type}? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeletingActivity(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTabWithModals;
